import { useEffect, useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { updateSEO, seoConfigs } from '../../utils/seo';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    service: '',
    date: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    window.scrollTo(0, 0);
    updateSEO(seoConfigs.contact);
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name || !formData.phone || !formData.email) {
      alert('お名前、電話番号、メールアドレスは必須項目です。');
      return;
    }

    if (formData.message.length > 500) {
      alert('お問い合わせ内容は500文字以内で入力してください。');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('phone', formData.phone);
      formBody.append('email', formData.email);
      formBody.append('service', formData.service);
      formBody.append('date', formData.date);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d5u1lo1dkj8fk0537nmg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          phone: '',
          email: '',
          service: '',
          date: '',
          message: '',
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative h-[30vh] md:h-[35vh] w-full overflow-hidden">
        <div className="absolute inset-0 w-full h-full">
          <img
            src="https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/e23bf2646bc6317ae25adf87a49906d2.jpeg"
            alt="岡山市の脱毛サロン・エステのスタッフ"
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        
        <div className="relative z-10 h-full flex items-center justify-center">
          <div className="text-center px-6">
            <p className="text-white text-xs md:text-sm font-light mb-4 tracking-widest uppercase">
              Contact
            </p>
            <h1 className="text-white text-4xl md:text-6xl lg:text-7xl font-bold">
              予約・お問い合わせ
            </h1>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-20 md:py-28 bg-white px-6">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-5xl font-bold text-gray-dark mb-8 text-center">
              お問い合わせフォーム
            </h2>

            <form id="contact-form" onSubmit={handleSubmit} data-readdy-form className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-dark mb-2">
                  お名前 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-0 py-3 bg-transparent border-b-2 border-gray-medium/30 text-gray-dark text-base font-light focus:outline-none focus:border-gray-dark transition-colors"
                  placeholder="山田 花子"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-dark mb-2">
                  電話番号 <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-0 py-3 bg-transparent border-b-2 border-gray-medium/30 text-gray-dark text-base font-light focus:outline-none focus:border-gray-dark transition-colors"
                  placeholder="090-1234-5678"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-dark mb-2">
                  メールアドレス <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-0 py-3 bg-transparent border-b-2 border-gray-medium/30 text-gray-dark text-base font-light focus:outline-none focus:border-gray-dark transition-colors"
                  placeholder="example@email.com"
                />
              </div>

              <div>
                <label htmlFor="service" className="block text-sm font-medium text-gray-dark mb-2">
                  ご希望のサービス
                </label>
                <select
                  id="service"
                  name="service"
                  value={formData.service}
                  onChange={handleChange}
                  className="w-full px-0 py-3 bg-transparent border-b-2 border-gray-medium/30 text-gray-dark text-base font-light focus:outline-none focus:border-gray-dark transition-colors cursor-pointer"
                >
                  <option value="">選択してください</option>
                  <option value="全身脱毛コース">全身脱毛コース</option>
                  <option value="部分脱毛プラン">部分脱毛プラン</option>
                  <option value="フェイシャルエステ">フェイシャルエステ</option>
                  <option value="美白フェイシャル">美白フェイシャル</option>
                  <option value="痩身ボディケア">痩身ボディケア</option>
                  <option value="ブライダルコース">ブライダルコース</option>
                  <option value="その他・相談">その他・相談</option>
                </select>
              </div>

              <div>
                <label htmlFor="date" className="block text-sm font-medium text-gray-dark mb-2">
                  ご希望日時
                </label>
                <input
                  type="text"
                  id="date"
                  name="date"
                  value={formData.date}
                  onChange={handleChange}
                  className="w-full px-0 py-3 bg-transparent border-b-2 border-gray-medium/30 text-gray-dark text-base font-light focus:outline-none focus:border-gray-dark transition-colors"
                  placeholder="例：3月25日 14:00〜"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-dark mb-2">
                  お問い合わせ内容
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  maxLength={500}
                  rows={5}
                  className="w-full px-0 py-3 bg-transparent border-b-2 border-gray-medium/30 text-gray-dark text-base font-light focus:outline-none focus:border-gray-dark transition-colors resize-none"
                  placeholder="ご質問やご要望をお聞かせください（500文字以内）"
                ></textarea>
                <p className="text-xs text-gray-medium mt-2 text-right">
                  {formData.message.length} / 500文字
                </p>
              </div>

              {submitStatus === 'success' && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm text-green-800">
                    お問い合わせありがとうございます。内容を確認次第、担当者よりご連絡させていただきます。
                  </p>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-800">
                    送信に失敗しました。お手数ですが、お電話にてお問い合わせください。
                  </p>
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-8 py-4 bg-gray-dark text-white text-base font-medium rounded-full whitespace-nowrap hover:bg-gray-medium transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? '送信中...' : '送信する'}
              </button>

              <p className="text-xs text-gray-medium text-center leading-relaxed">
                ご入力いただいた個人情報は、お問い合わせ対応のみに使用し、適切に管理いたします。
              </p>
            </form>
          </div>
        </div>
      </section>

      {/* Contact Info Section */}
      <section className="py-20 md:py-28 bg-gray-light px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold text-gray-dark mb-6">
              お電話でのご予約
            </h2>
            <p className="text-base md:text-lg font-light text-gray-dark/80 max-w-3xl mx-auto leading-relaxed">
              お急ぎの方はお電話でのご予約が便利です
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <article className="bg-white rounded-lg p-8 text-center">
              <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: '#8C646E' }}>
                <i className="ri-phone-line text-4xl text-white w-12 h-12 flex items-center justify-center"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-dark mb-4">お電話</h3>
              <a
                href="tel:086-012-3456"
                className="text-2xl md:text-3xl font-bold text-gray-dark hover:text-gray-medium transition-colors cursor-pointer block mb-3"
              >
                086-012-3456
              </a>
              <p className="text-sm font-light text-gray-dark/70 leading-relaxed">
                受付時間：10:00〜19:00<br />
                定休日：水曜日
              </p>
            </article>

            <article className="bg-white rounded-lg p-8 text-center">
              <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: '#8C646E' }}>
                <i className="ri-mail-line text-4xl text-white w-12 h-12 flex items-center justify-center"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-dark mb-4">メール</h3>
              <a
                href="mailto:info@okayama-esthe.jp"
                className="text-lg md:text-xl font-medium text-gray-dark hover:text-gray-medium transition-colors cursor-pointer block mb-3 break-all"
              >
                info@okayama-esthe.jp
              </a>
              <p className="text-sm font-light text-gray-dark/70 leading-relaxed">
                24時間受付<br />
                2営業日以内に返信いたします
              </p>
            </article>

            <article className="bg-white rounded-lg p-8 text-center">
              <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: '#8C646E' }}>
                <i className="ri-line-line text-4xl text-white w-12 h-12 flex items-center justify-center"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-dark mb-4">LINE</h3>
              <a
                href="https://line.me/R/ti/p/@okayama-esthe"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block px-6 py-2.5 bg-[#06C755] text-white text-sm font-medium rounded-full whitespace-nowrap hover:bg-[#05B04D] transition-colors cursor-pointer mb-3"
              >
                友だち追加
              </a>
              <p className="text-sm font-light text-gray-dark/70 leading-relaxed">
                24時間受付<br />
                お得な情報も配信中
              </p>
            </article>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;